// components/ProtectedRoute.tsx
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import type { RootState, AppDispatch } from '../store/store';
import { getUserInfo } from '../services/auth';
import { fetchExams } from '../services/exams';
import { logout } from '../slices/auth/authSlice';
import { userLoading, userFailed } from '../slices/user/userSlice';
import { examsLoading, examsFailed } from '../slices/exams/examsSlice';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({
  children,
}: ProtectedRouteProps): JSX.Element | null => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();

  const {
    isAuthenticated,
    status: authStatus,
    authToken, // Keep authToken to check if initial check might be pending
  } = useSelector((state: RootState) => state.auth);
  const { profile, status: userStatus } = useSelector(
    (state: RootState) => state.user
  );
  const { status: examsStatus } = useSelector(
    (state: RootState) => state.exams
  );

  const isPotentiallyAuthenticating =
    authStatus === 'loading' || (authStatus === 'idle' && !!authToken);
  const isDataLoading = userStatus === 'loading' || examsStatus === 'loading';
  const needsDataFetch =
    isAuthenticated && !profile && !isDataLoading && userStatus !== 'failed';

  useEffect(() => {
    if (needsDataFetch) {
      const fetchData = async () => {
        dispatch(userLoading());
        dispatch(examsLoading());
        try {
          const userInfo = await getUserInfo(); // Dispatches userReceived internally
          await fetchExams(); // Dispatches examsReceived internally

          // Handle explicit anonymous user case trying to access protected areas
          // Note: /library and /demo are handled by the redirect logic below if !isAuthenticated
          if (userInfo?.isAnon) {
            const isAllowedAnonPath =
              location.pathname === '/demo' ||
              location.pathname.startsWith('/demo/');

            if (!isAllowedAnonPath) {
              console.log(
                'ProtectedRoute: Anon user on protected path, logging out.'
              );
              dispatch(logout());
            }
            // If on allowed path, continue rendering (auth state might still be true briefly)
          } else if (!userInfo && isAuthenticated) {
            // If getUserInfo failed silently but we are still authenticated somehow
            console.error(
              'ProtectedRoute: getUserInfo returned no data while authenticated, logging out.'
            );
            dispatch(logout());
          }
        } catch (error) {
          console.error(
            'ProtectedRoute: Data fetch failed, logging out.',
            error
          );
          // Services might dispatch specific failures, but logout is the safe fallback
          dispatch(
            userFailed(
              error instanceof Error
                ? error.message
                : 'Failed to fetch user data'
            )
          );
          dispatch(
            examsFailed(
              error instanceof Error
                ? error.message
                : 'Failed to fetch exam data'
            )
          );
          dispatch(logout());
        }
      };
      fetchData();
    }
  }, [dispatch, needsDataFetch, isAuthenticated, location.pathname]);

  // --- Render Logic ---

  // 1. Show loader if initial auth check is running
  if (isPotentiallyAuthenticating && !isAuthenticated) {
    return <LoadingSpinner />;
  }

  // 2. Handle redirection if not authenticated
  if (!isAuthenticated) {
    // Allow specific public paths within the protected layout structure
    const isAllowedPublicPath =
      location.pathname === '/demo' || location.pathname.startsWith('/demo/');

    if (isAllowedPublicPath) {
      // Render the child component (e.g., Library/Demo), which should handle the !isAuthenticated state itself
      return <>{children}</>;
    } else {
      return (
        <Navigate
          to={`/auth${location.search}`}
          state={{ from: location }}
          replace
        />
      );
    }
  }

  // 3. Authenticated: Show loader while fetching essential data
  // Check if profile exists as the primary gatekeeper after authentication
  if (!profile) {
    // If data fetch failed, the useEffect should trigger logout, leading back to step 2.
    // So, we only show loading spinner here if loading or profile hasn't populated yet.
    return <LoadingSpinner />;
  }

  // 4. Authenticated and essential data loaded
  // Render the protected content
  return <>{children}</>;
};

// Simple loading spinner component (keep as is or use your app's standard loader)
const LoadingSpinner = () => (
  <div
    style={{
      height: '100vh', // Make it full viewport height for clarity
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress />
  </div>
);

export default ProtectedRoute;
